import React, { useMemo, useRef, useEffect } from 'react';
import * as constantsViewer from '../../constants';
import { useSpring, a } from 'react-spring-beta/three';
import { Rack } from './Rack';
import { Door } from './Door';
import { Drawer } from './Drawer';
import { Rod } from '../pieces/Rod';
import { IntermediateShelf } from '../pieces/IntermediateShelf';
import {
  getRacksWithPositions,
  getDrawersWithPositions,
  getInterShelvesWithPositions,
  onPointerDown,
  onPointerUp,
  manageOnClickShelf,
} from '../../helpers';
import { useDispatch } from 'react-redux';
import {
  addToShelvesSelectionAction,
  shelvesSelectionAction,
} from '../../../Configurator/actions/selection';
import { selectCabinetFromIdAction } from '../../../Configurator/actions/furniture';

export function Shelf({
  multiCabinets,
  cabinetId,
  configuratorMode,
  cabinetWidth,
  cabinetDepth,
  shelf,
  position = [0, 0, 0],
  unselected = false,
}) {
  const group = useRef();

  const [spring, set] = useSpring(() => ({
    position: position,
    immediate: !constantsViewer.REACT_SPRING_ANIMATIONS_ACTIVE,
    config: constantsViewer.REACT_SPRING_ANIMATIONS_CONF,
  }));

  const dispatch = useDispatch();

  const shelfHeight = shelf.realHeight;
  const racksWithPositions = useMemo(() => getRacksWithPositions(shelf), [
    shelf.racks,
  ]);
  const drawersWithPositions = useMemo(() => getDrawersWithPositions(shelf), [
    shelf.drawers,
  ]);
  const interShelvesWithPositions = useMemo(
    () => getInterShelvesWithPositions(shelf),
    [shelf.intermediatesShelves],
  );

  useEffect(() => {
    let self = group.current;
    if (self) {
      set({
        position: position,
      });
    }
  });

  return (
    <a.group
      ref={group}
      onPointerDown={e => onPointerDown(e)}
      onPointerUp={e =>
        onPointerUp(e, e => {
          manageOnClickShelf(
            e,
            multiCabinets,
            cabinetId,
            dispatch,
            configuratorMode,
            shelf.level,
          );
        })
      }
      position={spring.position}
      onUpdate={self => {
        //console.debug('Shelf height:', shelfHeight);
      }}
    >
      {racksWithPositions.map(positionRack => {
        const rack = positionRack.rack;
        //console.debug('positionRack.position:', positionRack.position);

        return (
          <Rack
            cabinetWidth={cabinetWidth}
            cabinetDepth={cabinetDepth}
            rack={rack}
            cabinetId={cabinetId}
            shelfLevel={shelf.level}
            configuratorMode={configuratorMode}
            unselected={unselected}
            position={[
              0,
              positionRack.position - (shelfHeight - rack.realHeight) / 2,
              0,
            ]}
            key={rack.level}
          />
        );
      })}

      {drawersWithPositions.map(positionDrawer => {
        const drawer = positionDrawer.drawer;

        return (
          <Drawer
            cabinetWidth={cabinetWidth}
            cabinetDepth={cabinetDepth}
            drawer={drawer}
            cabinetId={cabinetId}
            shelfLevel={shelf.level}
            configuratorMode={configuratorMode}
            level={positionDrawer.level}
            unselected={unselected}
            position={[
              0,
              positionDrawer.position - (shelfHeight - 40 - drawer.height) / 2,
              0,
            ]}
            key={positionDrawer.level}
          />
        );
      })}

      {interShelvesWithPositions.map(positionIS => {
        const interShelf = positionIS.interShelf;

        return (
          <IntermediateShelf
            intermediateShelf={interShelf}
            cabinetId={cabinetId}
            shelfLevel={shelf.level}
            configuratorMode={configuratorMode}
            yOffset={positionIS.position}
            cabinetWidth={cabinetWidth}
            cabinetDepth={cabinetDepth}
            shelfHeight={shelfHeight - 40}
            unselected={unselected}
            key={interShelf.position}
          />
        );
      })}

      {shelf.doors.map(door => {
        return (
          <Door
            door={door}
            cabinetId={cabinetId}
            shelfLevel={shelf.level}
            configuratorMode={configuratorMode}
            cabinetWidth={cabinetWidth}
            cabinetDepth={cabinetDepth}
            shelfHeight={shelfHeight - 40}
            unselected={unselected}
            key={door.position}
          />
        );
      })}

      {shelf.rod ? (
        <Rod
          cabinetWidth={cabinetWidth}
          cabinetDepth={cabinetDepth}
          shelfHeight={shelfHeight}
          unselected={unselected}
        />
      ) : null}
    </a.group>
  );
}
